import { Component } from '@theme/component';
import { onAnimationEnd } from '@theme/utilities';
import { ThemeEvents, CartUpdateEvent } from '@theme/events';

/**
 * A custom element that displays a cart icon.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} cartBubble - The cart bubble element.
 * @property {HTMLElement} cartBubbleText - The cart bubble text element.
 * @property {HTMLElement} cartBubbleCount - The cart bubble count element.
 *
 * @extends {Component<Refs>}
 */
class CartIcon extends Component {
  requiredRefs = ['cartBubble', 'cartBubbleText', 'cartBubbleCount'];

  /** @type {number} */
  get currentCartCount() {
    const parsedCount = parseInt(this.refs.cartBubbleCount.textContent ?? '0', 10);
    return Number.isFinite(parsedCount) ? parsedCount : 0;
  }

  set currentCartCount(value) {
    this.refs.cartBubbleCount.textContent = value < 100 ? String(value) : '';
  }

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
    window.addEventListener('pageshow', this.onPageShow);
    this.ensureCartBubbleIsCorrect();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
    window.removeEventListener('pageshow', this.onPageShow);
  }

  /**
   * Handles the page show event when the page is restored from cache.
   * @param {PageTransitionEvent} event - The page show event.
   */
  onPageShow = (event) => {
    if (event.persisted) {
      this.ensureCartBubbleIsCorrect();
    }
  };

  /**
   * Handles the cart update event.
   * @param {CartUpdateEvent} event - The cart update event.
   */
  onCartUpdate = async (event) => {
    const updateSource = event.detail?.data?.source;
    const countFromResource = this.getCountFromResource(event.detail?.resource);

    if (countFromResource !== null) {
      this.renderCartBubble(countFromResource);
      return;
    }

    const fallbackCount = Number(event.detail?.data?.itemCount);
    if (Number.isFinite(fallbackCount)) {
      // Product-form events send itemCount as a delta (added amount), not cart total.
      if (updateSource === 'product-form-component') {
        const optimisticCount = this.currentCartCount + Math.max(0, fallbackCount);
        this.renderCartBubble(optimisticCount);
        void this.syncCartBubbleFromCart();
        return;
      }

      this.renderCartBubble(Math.max(0, fallbackCount));
      return;
    }

    await this.syncCartBubbleFromCart();
  };

  /**
   * Renders the cart bubble.
   * @param {number} itemCount - The number of items in the cart.
   * @param {boolean} comingFromProductForm - Whether the cart update is coming from the product form.
   */
  renderCartBubble = async (itemCount, animate = true) => {
    const normalizedCount = Math.max(0, Number(itemCount) || 0);

    this.refs.cartBubbleCount.classList.toggle('hidden', normalizedCount === 0);
    this.refs.cartBubble.classList.toggle('visually-hidden', normalizedCount === 0);

    this.currentCartCount = normalizedCount;
    this.classList.toggle('header-actions__cart-icon--has-cart', normalizedCount > 0);

    sessionStorage.setItem(
      'cart-count',
      JSON.stringify({
        value: String(this.currentCartCount),
        timestamp: Date.now(),
      })
    );

    if (!animate || normalizedCount === 0) return;

    // Ensure element is visible before starting animation
    // Use requestAnimationFrame to ensure the browser sees the state change
    await new Promise((resolve) => requestAnimationFrame(resolve));

    this.refs.cartBubble.classList.add('cart-bubble--animating');
    await onAnimationEnd(this.refs.cartBubbleText);

    this.refs.cartBubble.classList.remove('cart-bubble--animating');
  };

  /**
   * Checks if the cart count is correct.
   */
  ensureCartBubbleIsCorrect = () => {
    // Ensure refs are available
    if (!this.refs.cartBubbleCount) return;

    const sessionStorageCount = sessionStorage.getItem('cart-count');

    // If no session storage data, nothing to check
    if (sessionStorageCount === null) return;

    const visibleCount = this.refs.cartBubbleCount.textContent;

    try {
      const { value, timestamp } = JSON.parse(sessionStorageCount);

      // Check if the stored count matches what's visible
      if (value === visibleCount) return;

      // Only update if timestamp is recent (within 10 seconds)
      if (Date.now() - timestamp < 10000) {
        const count = parseInt(value, 10);

        if (count >= 0) {
          this.renderCartBubble(count, false);
        }
      }
    } catch (_) {
      // no-op
    }
  };

  /**
   * Attempts to read an exact cart item count from event resource payloads.
   * @param {unknown} resource
   * @returns {number | null}
   */
  getCountFromResource(resource) {
    if (!resource || typeof resource !== 'object') return null;

    const asResource = /** @type {{ item_count?: unknown; items?: Array<{ quantity?: unknown }> }} */ (resource);
    const itemCount = Number(asResource.item_count);
    if (Number.isFinite(itemCount)) return Math.max(0, itemCount);

    if (Array.isArray(asResource.items)) {
      const total = asResource.items.reduce((sum, item) => {
        const quantity = Number(item?.quantity);
        return Number.isFinite(quantity) ? sum + quantity : sum;
      }, 0);
      return Math.max(0, total);
    }

    return null;
  }

  /**
   * Fetches cart state and renders exact bubble count.
   */
  syncCartBubbleFromCart = async () => {
    try {
      const response = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
      if (!response.ok) return;

      const cart = await response.json();
      const count = this.getCountFromResource(cart);
      if (count !== null) {
        await this.renderCartBubble(count);
      }
    } catch (_) {
      // no-op
    }
  };
}

if (!customElements.get('cart-icon')) {
  customElements.define('cart-icon', CartIcon);
}
